import { KTSelect } from "./select";
import { defaultTemplates, Templates } from "./templates";
import {
	debounce,
	handleDropdownKeyNavigation,
	filterOptions,
	FocusManager,
	EventManager
} from "./utils";

export class KTSelectSearch {
	private _select: KTSelect;
	private _searchInput: HTMLInputElement;
	private _noResultsElement: HTMLElement | null = null;
	private _originalOptionContents = new Map<string, string>();
	private _boundKeyNavHandler: (event: KeyboardEvent) => void;
	private _eventManager: EventManager;
	private _focusManager: FocusManager;

	// Public handler for search input (made public for event binding)
	public handleSearchInput: (...args: any[]) => void;

	constructor(select: KTSelect) {
		this._select = select;
		this._searchInput = select.getSearchInput();
		this._eventManager = new EventManager();

		// Initialize focus manager after dropdown element is available
		this._focusManager = new FocusManager(this._select.getDropdownElement());

		// Create bound handler references to allow proper cleanup
		this._boundKeyNavHandler = this._handleKeyboardNavigation.bind(this);

		// Bind the search input handler with context
		this.handleSearchInput = this._handleSearchInput.bind(this);

		// Cache original option contents for later restoration
		this._cacheOriginalOptionContents();
	}

	init() {
		if (this._select.getConfig().enableSearch) {
			this._searchInput = this._select.getSearchInput();

			if (this._searchInput) {
				console.log('Initializing search module with input:', this._searchInput);

				// First remove any existing listeners to prevent duplicates
				this._removeEventListeners();

				// Add the event listener
				this._eventManager.addListener(
					this._searchInput,
					'input',
					this.handleSearchInput
				);

				// Add blur event listener to ensure highlights are cleared when focus is lost
				this._eventManager.addListener(
					this._searchInput,
					'blur',
					() => {
						if (!this._searchInput.value) {
							this._resetAllOptions();
						}
					}
				);

				// Listen for remote search events to coordinate with remote search functionality
				if (this._select.getConfig().remote && this._select.getConfig().searchParam) {
					this._select.getElement().addEventListener('ktselect.remoteSearchStart', () => {
						// Reset focused option when remote search starts
						this._focusManager.resetFocus();
					});

					this._select.getElement().addEventListener('ktselect.remoteSearchEnd', () => {
						// After remote search completes, refresh our option cache
						this.refreshOptionCache();
					});
				}

				// Add keyboard navigation for search results
				// This is stopping event propagation to prevent conflicts
				this._eventManager.addListener(
					this._searchInput,
					'keydown',
					this._boundKeyNavHandler
				);

				// Listen for dropdown close to reset options if search is empty
				this._select.getElement().addEventListener('dropdown.close', () => {
					this._focusManager.resetFocus();
					if (!this._searchInput.value) {
						this._resetAllOptions();
					}
				});

				// Clear highlights when an option is selected
				this._select.getElement().addEventListener('ktselect.change', () => {
					this.clearSearchHighlights();

					// Close dropdown if configured to do so
					if (this._select.getConfig().closeOnSelect && !this._select.getConfig().multiple) {
						this._select.closeDropdown();
					}
				});

				// Autofocus on search input
				if (this._select.getConfig().searchAutofocus) {
					this._select.getElement().addEventListener('dropdown.show', () => {
						setTimeout(() => {
							// Add slight delay to ensure the dropdown and search input are visible
							this._searchInput?.focus();
						}, 50);
					});
				}
			}
		}
	}

	/**
	 * Remove event listeners to prevent memory leaks or duplicates
	 */
	private _removeEventListeners(): void {
		if (this._searchInput) {
			this._eventManager.removeAllListeners(this._searchInput);
		}
	}

	/**
	 * Handle keyboard navigation for search results
	 */
	private _handleKeyboardNavigation(event: KeyboardEvent) {
		// Stop propagation to prevent multiple handlers from firing
		event.stopPropagation();

		console.log('Search module keydown:', event.key);

		const visibleOptions = this._focusManager.getVisibleOptions();
		if (visibleOptions.length === 0) return;

		// Use the shared keyboard navigation handler with custom callbacks
		handleDropdownKeyNavigation(
			event,
			this._select,
			{
				multiple: this._select.getConfig().multiple,
				closeOnSelect: this._select.getConfig().closeOnSelect
			},
			{
				onArrowDown: () => this._focusManager.focusNext(),
				onArrowUp: () => this._focusManager.focusPrevious(),
				onEnter: () => this._selectFocusedOption(),
				onClose: () => {
					if (event.key === 'Escape') {
						this.clearSearchHighlights();
					}
				}
			}
		);
	}

	/**
	 * Select the currently focused option
	 */
	private _selectFocusedOption() {
		const focusedOption = this._focusManager.getFocusedOption();

		if (focusedOption) {
			const optionValue = focusedOption.getAttribute('data-value');

			if (optionValue) {
				// Trigger the selection in the main select component
				this._select['_selectOption'](optionValue);
			}
		}
	}

	/**
	 * Store original HTML content of all options for later restoration
	 * This prevents losing formatting when clearing search
	 */
	private _cacheOriginalOptionContents() {
		// Wait for options to be initialized
		setTimeout(() => {
			const options = Array.from(this._select.getOptionsElement());
			options.forEach(option => {
				const value = option.getAttribute('data-value');
				if (value) {
					this._originalOptionContents.set(value, option.innerHTML);
				}
			});
		}, 0);
	}

	private _handleSearchInput(event: Event) {
		const query = (event.target as HTMLInputElement).value.toLowerCase();
		const config = this._select.getConfig();

		// Reset focused option when search changes
		this._focusManager.resetFocus();

		// For remote search, we don't filter locally
		// The KTSelect component will handle the remote search
		if (config.remote && config.searchParam) {
			// If query is too short, reset all options to visible state
			if (query.length < config.searchMinLength) {
				this._resetAllOptions();
				this._clearNoResultsMessage();
			}
			// Otherwise, let KTSelect handle remote search
			return;
		}

		// For local search
		if (query.length >= config.searchMinLength) {
			this._filterOptions(query);
		} else {
			this._resetAllOptions();
			this._clearNoResultsMessage();
		}
	}

	private _filterOptions(query: string) {
		const options = Array.from(this._select.getOptionsElement()) as HTMLElement[];
		const config = this._select.getConfig();
		const dropdownElement = this._select.getDropdownElement();

		// Cache original option HTML if not already cached
		if (this._originalOptionContents.size === 0) {
			this._cacheOriginalOptionContents();
		}

		// Use the shared filterOptions utility
		filterOptions(
			options,
			query,
			config,
			dropdownElement,
			(visibleCount) => this._handleNoResults(visibleCount)
		);

		// Apply specialized text highlighting if needed
		if (config.searchHighlight && query.trim() !== '') {
			this._applyHighlightToDisplay(query);
		}
	}

	/**
	 * Apply highlighting to the display element for multi-select
	 */
	private _applyHighlightToDisplay(query: string) {
		// Implementation for display highlighting
	}

	/**
	 * Reset all options to their original state
	 */
	private _resetAllOptions() {
		// Show all options and reset their HTML content
		const options = Array.from(this._select.getOptionsElement());
		options.forEach(option => {
			(option as HTMLElement).style.display = 'block';

			// Restore from cache if available (fastest approach)
			const value = option.getAttribute('data-value');
			if (value && this._originalOptionContents.has(value)) {
				(option as HTMLElement).innerHTML = this._originalOptionContents.get(value);
			}
		});
	}

	/**
	 * Handle the case when no results are found
	 */
	private _handleNoResults(visibleOptionsCount: number) {
		if (visibleOptionsCount === 0) {
			this._showNoResultsMessage();
		} else {
			this._clearNoResultsMessage();
		}
	}

	private _showNoResultsMessage() {
		if (!this._noResultsElement) {
			const config = this._select.getConfig();
			const theme = Templates.getTheme();
			this._noResultsElement = defaultTemplates.noResults(config, theme);

			const optionsContainer = this._select.getDropdownElement().querySelector('[data-kt-select-options-container]');
			if (optionsContainer) {
				optionsContainer.appendChild(this._noResultsElement);
			} else {
				this._select.getDropdownElement().appendChild(this._noResultsElement);
			}
		}
	}

	private _clearNoResultsMessage() {
		if (this._noResultsElement) {
			this._noResultsElement.remove();
			this._noResultsElement = null;
		}
	}

	/**
	 * Public method to explicitly clear all search highlights
	 * Can be called when needed from outside
	 */
	public clearSearchHighlights() {
		if (this._searchInput) {
			this._searchInput.value = '';
		}
		this._resetAllOptions();
		this._clearNoResultsMessage();

		// Also clear highlights from the display element
		this._clearDisplayHighlights();
	}

	/**
	 * Clear any highlights from the display element (selected values)
	 */
	private _clearDisplayHighlights() {
		// Implementation for clearing display highlights
	}

	/**
	 * Refresh option cache after remote data is loaded
	 * This ensures that search highlighting works correctly with new options
	 */
	public refreshOptionCache(): void {
		// Clear existing cache
		this._originalOptionContents.clear();

		// Re-cache all options
		const options = Array.from(this._select.getOptionsElement());
		options.forEach(option => {
			const value = option.getAttribute('data-value');
			if (value) {
				this._originalOptionContents.set(value, option.innerHTML);
			}
		});
	}

	/**
	 * Destroy the search module and clean up
	 */
	public destroy(): void {
		this._removeEventListeners();
		this._originalOptionContents.clear();
	}
}